import { setLineStyle, setFontStyle } from "../canvasUtils";
import Annotation from "./annotation";

export default class ThicknessChart extends Annotation {
  constructor() {
    super();

    this.data = null;
    this.chartSize = null;
    this.scaledData = null;
    this.maxX = null;
    this.yRules = [0, 250, 500, 750, 1000];
    this.xRules = [];

    this.xInPerPx = 0;
    this.yInPerPx = 0;
    this.mmPerIn = 25.4;
    this.umPerMm = 1000;

    this.transform = null;
    this.sliceFit = null;
    this.sliceInfo = null;
    this.exam = null;

    this.lineWidth = 2;
    this.color = "#262626";
    this.ruleWidth = 40;
    this.gutterHeight = 23;

    this.font = "14px Calibri";
    this.baseline = "hanging";
    this.fontColor = "#00b0f0";
    this.dataColor = "#00b0f0";

    this.yLabel = "Thickness (μm)";
    this.xLabel = "Position (mm)";
    this.canvasSize = null;

    this.curXsize = 0;
    this.zoomXOffset = 0.5;
  }

  draw(ctx) {
    if (!this.sliceFit || !this.scaledData || !this.transform || !this.canvasSize) return;

    let w = this.canvasSize.w;
    let h = this.canvasSize.h;

    let chartW = this.sliceFit.w;
    let chartH = h - this.ruleWidth - this.gutterHeight;
    let zeroX = w - chartW;
    setLineStyle(ctx, this);
    this.drawBars(ctx, zeroX, chartH, chartW);
    this.drawRules(ctx, zeroX, chartH, chartW);
    setFontStyle(ctx, this);
    this.writeRules(ctx, zeroX, chartH, chartW);
    this.writeLabels(ctx, zeroX, chartH, chartW);
    ctx.strokeStyle = this.dataColor;
    this.drawData(ctx, zeroX, chartH, chartW);
  }

  drawBars(ctx, zeroX, chartH, chartW) {
    ctx.fillRect(zeroX - this.ruleWidth, 0, this.ruleWidth, chartH + this.ruleWidth);
    ctx.fillRect(zeroX - this.ruleWidth, chartH, chartW + this.ruleWidth, this.ruleWidth);
  }

  drawRules(ctx, zeroX, chartH, chartW) {
    this.yRules.forEach(value => {
      let y = this.convertY(chartH, value);
      if (y <= 0 || y >= chartH) return;
      ctx.beginPath();
      ctx.moveTo(zeroX, y);
      ctx.lineTo(zeroX + chartW, y);
      ctx.stroke();
      ctx.closePath();
    });

    this.xRules.forEach(value => {
      let x = this.convertX(zeroX, chartW, value);
      if (x <= zeroX || x >= (zeroX + chartW)) return;
      ctx.beginPath();
      ctx.moveTo(x, 0);
      ctx.lineTo(x, chartH);
      ctx.stroke();
      ctx.closePath();
    });
  }

  writeRules(ctx, zeroX, chartH, chartW) {
    this.yRules.forEach(value => {
      let y = this.convertY(chartH, value);
      if (y < 0 || y > chartH) return;
      this.writeYRule(ctx, zeroX, y, value);
    });

    this.xRules.forEach(value => {
      let x = this.convertX(zeroX, chartW, value);
      if (x < zeroX || x > (zeroX + chartW)) return;
      this.writeXRule(ctx, x, chartH, value);
    });
  }

  writeYRule(ctx, x, y, value) {
    let width = ctx.measureText(value).width;
    ctx.fillText(value, x - width - 10, y);
  }

  writeXRule(ctx, x, y, value) {
    let width = ctx.measureText(value).width;
    ctx.fillText(value, x - width / 2, y + 10);
  }

  writeLabels(ctx, zeroX, chartH, chartW) {
    let yLabelWidth = ctx.measureText(this.yLabel).width;
    let x = zeroX - yLabelWidth - this.ruleWidth - 5;
    let y = chartH / 2 - 7;
    ctx.save();
    ctx.translate(x + 60, y + 60);
    ctx.rotate(-Math.PI/2);
    ctx.fillText(this.yLabel, 0, 0);
    ctx.restore();

    let xLabelWidth = ctx.measureText(this.xLabel).width;
    x = zeroX + (chartW - xLabelWidth) / 2;
    y = chartH + this.ruleWidth + 5;
    ctx.fillText(this.xLabel, x, y);
  }

  drawData(ctx, zeroX, chartH, chartW) {
    ctx.beginPath();
    let start = true;
    this.scaledData.forEach((value, index) => {
      let x = this.convertX(zeroX, chartW, index * this.xInPerPx * this.mmPerIn);
      if (x <= zeroX || x >= (zeroX + chartW)) return;
      let y = this.convertY(chartH, value);
      if (y <= 0 || y >= chartH) return;
      if (start) {
        ctx.moveTo(x, y);
        start = false;
      }
      else ctx.lineTo(x, y);
    })
    ctx.stroke();
    ctx.closePath();
  }

  convertX(zeroX, chartW, x) {
    let fitZoom = this.transform.fitZoom;
    let zoom = this.transform.zoom;
    let offsetX = this.transform.offsetX;

    return zeroX + x * (chartW * zoom) / (this.maxX * fitZoom) + offsetX;
  }

  convertY(chartH, y) {
    return chartH * (1 - y / 1000);
  }

  updateData(data) {
    if (this.data !== data) this.data = data;
    this.updateScaledData();
    this.signalReady();
  }

  updateChartSize(chartSize) {
    if (this.chartSize !== chartSize) this.chartSize = chartSize;
    this.signalReady();
  }

  updateSliceFit(fit) {
    if (this.sliceFit !== fit) this.sliceFit = fit;
    this.signalReady();
  }

  updateTransform(transform) {
    if (this.transform !== transform) this.transform = transform;
    this.signalReady();
  }

  updateSliceInfo(sliceInfo) {
    if (this.sliceInfo !== sliceInfo) this.sliceInfo = sliceInfo;
    if (this.sliceInfo) {
      this.xInPerPx = 1 / this.sliceInfo.dimensions.horizontalResolution;
      this.yInPerPx = 1 / this.sliceInfo.dimensions.verticalResolution;

      let pxWidth = this.sliceInfo.segmentation.originalPixelWidth;
      let xPxPerMm = this.sliceInfo.dimensions.horizontalResolution / this.mmPerIn;
      this.curXsize = pxWidth / xPxPerMm;
    }
    this.updateScaledData();
    this.signalReady();
  }

  updateScaledData() {
    if (this.sliceInfo && this.data) {
      this.scaledData = this.data.map(v => v * this.yInPerPx * this.mmPerIn * this.umPerMm);
      let zoomCenter = this.zoomXOffset * this.curXsize;
      let imageCenter = this.curXsize / 2;
      this.maxX = zoomCenter + ((this.curXsize - imageCenter) / 1);
      this.generateXRules();
    }
  }

  generateXRules() {
    this.xRules = [];
    let value = 0.0;
    while (value < this.maxX) {
      this.xRules.push(value);
      value += 0.5;
    }
  }
}